
import React from 'react';
import { Subscription } from '@/types/subscription';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { MapPin, Mail, Trash2, Building2 } from 'lucide-react';
import { formatDate, getStatusBadge, getSubscriptionTypeBadge } from '@/utils/subscriptionFormatters';

interface SubscriptionRowProps {
  subscription: Subscription;
  cancellingId: string | null;
  onCancel: (subscription: Subscription) => void;
}

export function SubscriptionRow({ 
  subscription, 
  cancellingId, 
  onCancel 
}: SubscriptionRowProps) {
  return (
    <TableRow>
      <TableCell>
        <div className="space-y-2">
          <div>
            <div className="font-medium text-base">
              {subscription.customerName}
            </div>
            {subscription.isBusiness && subscription.businessName && (
              <div className="text-sm text-blue-700 font-medium flex items-center gap-1 mt-1">
                <Building2 className="h-3 w-3" />
                {subscription.businessName}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Mail className="h-3 w-3" />
            {subscription.email}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-start gap-1 text-sm">
          <MapPin className="h-3 w-3 mt-0.5 text-gray-400" />
          <div>
            <div>{subscription.address}</div>
            <div className="text-gray-500">{subscription.postcode}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="capitalize">
          {subscription.multipleDays ? (
            <span>Multiple Days</span>
          ) : (
            subscription.binDay
          )}
          {subscription.multipleDays && (
            <Badge variant="secondary" className="ml-2 text-xs">Multiple Days</Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          {getSubscriptionTypeBadge(subscription)}
          {subscription.isBusiness && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 block w-fit">
              <Building2 className="h-3 w-3 mr-1" />
              Business
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>{getStatusBadge(subscription)}</TableCell>
      <TableCell>{formatDate(subscription.startDate)}</TableCell>
      <TableCell>£{(subscription.totalPaid || 0).toFixed(2)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {subscription.active && subscription.status !== 'cancelled' && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onCancel(subscription)}
              disabled={cancellingId === subscription.id}
            >
              {cancellingId === subscription.id ? (
                "Cancelling..."
              ) : (
                <>
                  <Trash2 className="h-3 w-3 mr-1" />
                  Cancel
                </>
              )}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
